import React, { useMemo, useState } from 'react';
import { Customer } from '../../types';
import {
    buildMergeUpdate,
    ImportedCustomerRow,
    MergeChoices,
    MergeFieldKey,
    mergeFieldDiffs,
    numberTakenByOther,
} from '../../services/customerListImport';
import { Button } from '../Button';
import { GitMerge, Loader2, X } from 'lucide-react';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

/**
 * Feld-weises Zusammenfuehren einer Listen-Zeile mit einem Bestandskunden:
 * je Feld Radio Liste vs. Bestand, Default Bestand. Die Kunden-ID bleibt —
 * Projekte, Angebote, Rechnungen und Umsaetze haengen weiter am Profil.
 *
 * Gibt es mehrere Dubletten-Kandidaten, wird das Ziel oben GEWAEHLT: vorher
 * ging jede Zusammenfuehrung stumm gegen den bestbewerteten Treffer, und bei
 * zwei aehnlich benannten Bestandskunden traf sie mit 50 % den falschen.
 */
interface Props {
    row: ImportedCustomerRow;
    /** Vorausgewaehltes Ziel. */
    existing: Customer;
    /** Alle in Frage kommenden Ziele; leer/1 Eintrag = keine Auswahl noetig. */
    candidates?: Customer[];
    allCustomers: Customer[];
    onApply: (updates: Partial<Customer>, target: Customer) => Promise<void>;
    onClose: () => void;
}

const FIELD_LABELS: Record<MergeFieldKey, string> = {
    customerNumber: 'Kundennummer',
    name: 'Name / Firma',
    firstName: 'Vorname',
    lastName: 'Nachname',
    street: 'Straße',
    zip: 'PLZ',
    city: 'Ort',
    email: 'E-Mail',
    phone: 'Telefon',
    salutation: 'Anrede',
    title: 'Titel',
    customerType: 'Kundentyp',
};

/** Rohwert eines Feldes lesbar — customerType ist intern englisch codiert. */
const fieldText = (key: MergeFieldKey, value: string | undefined): string => {
    if (!value) return '';
    if (key === 'customerType') return value === 'person' ? 'Privatperson' : 'Unternehmen';
    return value;
};

/** Kandidat in der Zielauswahl: Name, Nummer und Ort zur Unterscheidung. */
const candidateLabel = (c: Customer): string =>
    [c.name, c.customerNumber ? `Nr. ${c.customerNumber}` : '', [c.zip, c.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(' — ');

export const ImportMergeDialog: React.FC<Props> = ({ row, existing, candidates, allCustomers, onApply, onClose }) => {
    // Vorauswahl zuerst, dann die uebrigen Kandidaten — ohne Doppelte.
    const targets = useMemo(() => {
        const out: Customer[] = [existing];
        for (const c of candidates ?? []) if (!out.some((t) => t.id === c.id)) out.push(c);
        return out;
    }, [existing, candidates]);
    const [targetId, setTargetId] = useState(existing.id);
    const target = targets.find((t) => t.id === targetId) ?? existing;

    const diffs = useMemo(() => mergeFieldDiffs(row, target), [row, target]);
    // Default je Feld: Bestand. Fehlender Eintrag wird genauso gelesen, darum
    // startet die Wahl leer und wird beim Zielwechsel wieder geleert.
    const [choices, setChoices] = useState<MergeChoices>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Nummer aus der Liste nur waehlbar, wenn kein ANDERER Kunde sie haelt.
    const numberBlocked = numberTakenByOther(row.customerNumber, allCustomers, target.id);

    const apply = async () => {
        setSaving(true);
        setError(null);
        try {
            await onApply(buildMergeUpdate(row, target, choices), target);
        } catch (e) {
            setError(rechtFehlerOder(e));
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-amber-500/20 p-2 rounded-xl border border-amber-500/30"><GitMerge className="w-5 h-5 text-amber-400" /></div>
                        <div>
                            <h3 className="font-bold">Zusammenführen: {row.name}</h3>
                            <p className="text-xs text-slate-400">mit Bestandskunde „{target.name}“ — je Feld wählen, was gilt.</p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={saving} className="hover:bg-white/10 p-2 rounded-full transition-all disabled:opacity-40"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {targets.length > 1 && (
                        <div className="mb-4">
                            <label htmlFor="merge-target" className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                                Ziel-Kunde ({targets.length} Kandidaten)
                            </label>
                            <select
                                id="merge-target"
                                value={target.id}
                                disabled={saving}
                                onChange={(e) => {
                                    setTargetId(e.target.value);
                                    setChoices({});
                                }}
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                            >
                                {targets.map((c) => (
                                    <option key={c.id} value={c.id}>{candidateLabel(c)}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-slate-500 mt-1">
                                Mehrere Bestandskunden passen zu dieser Zeile — die Felder unten beziehen sich auf den gewählten.
                            </p>
                        </div>
                    )}
                    {diffs.length === 0 ? (
                        <p className="text-sm text-slate-500">Keine abweichenden Felder — die Liste bringt nichts Neues für diesen Kunden.</p>
                    ) : (
                        <table className="w-full text-left text-sm border border-slate-200 rounded-xl overflow-hidden">
                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[11px] tracking-wider">
                                <tr>
                                    <th className="p-3 w-[130px]">Feld</th>
                                    <th className="p-3">Liste</th>
                                    <th className="p-3">Bestand</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {diffs.map((key) => {
                                    const blocked = key === 'customerNumber' && numberBlocked;
                                    return (
                                        <tr key={key}>
                                            <td className="p-3 font-medium text-slate-600">{FIELD_LABELS[key]}</td>
                                            <td className="p-3">
                                                <label className={`flex items-start gap-2 ${blocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                                                    <input
                                                        type="radio"
                                                        name={`merge-${key}`}
                                                        disabled={blocked}
                                                        checked={choices[key] === 'list'}
                                                        onChange={() => setChoices({ ...choices, [key]: 'list' })}
                                                        className="mt-0.5"
                                                    />
                                                    <span>{fieldText(key, row[key]) || <span className="text-slate-300">—</span>}</span>
                                                </label>
                                                {blocked && (
                                                    <p className="text-[11px] text-red-600 mt-1">Nummer ist bereits an einen anderen Kunden vergeben.</p>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <label className="flex items-start gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`merge-${key}`}
                                                        checked={choices[key] !== 'list'}
                                                        onChange={() => setChoices({ ...choices, [key]: 'existing' })}
                                                        className="mt-0.5"
                                                    />
                                                    <span>{fieldText(key, target[key] as string | undefined) || <span className="text-slate-300">—</span>}</span>
                                                </label>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-between items-center shrink-0">
                    <span className="text-xs text-slate-500">Projekte, Angebote und Umsätze bleiben am Bestandskunden.</span>
                    <div className="flex gap-3">
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Abbrechen</Button>
                        <Button
                            variant="primary"
                            onClick={() => void apply()}
                            disabled={saving}
                            icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
                        >
                            Zusammenführen
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
